"use client";

import Link from "next/link";
import { LazyMotion, domAnimation, m } from "framer-motion";
import CtaButton from "@/components/ui/CtaButton";
import TrackedPhoneLink from "@/components/ui/TrackedPhoneLink";
import { PASSWORLD_MAP_EMBED_URL, PASSWORLD_MAP_TITLE } from "@/lib/map-embed";

type ImageProps = {
    src: string;
    alt?: string;
};

type Map = {
    url: string;
    image: ImageProps;
};

type Props = {
    tagline?: string;
    heading: string;
    description: string;
    map: Map;
};

export type MapSectionProps = React.ComponentPropsWithoutRef<"section"> & Partial<Props>;

const slideUp = {
    hidden: { y: 30, opacity: 0 },
    visible: (i: number) => ({
        y: 0,
        opacity: 1,
        transition: { duration: 0.8, ease: [0.215, 0.61, 0.355, 1], delay: i * 0.1 },
    }),
} as const;

export const MapSection = (props: MapSectionProps) => {
    const { heading, description } = {
        ...MapSectionDefaults,
        ...props,
    };
    return (
        <LazyMotion features={domAnimation}>
            <m.section
                id="relume"
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, amount: 0.1 }}
                className="px-[5%] pt-10 pb-0 -mb-8 md:pt-14 md:pb-0 md:-mb-12 lg:pt-18 lg:pb-0 lg:-mb-16"
            >
                <div className="container">
                    <m.div
                        variants={slideUp}
                        custom={0}
                        className="rb-12 mb-12 max-w-lg md:mb-18 lg:mb-20 will-change-transform"
                    >
                        <h2 className="rb-5 mb-5 text-5xl font-medium md:mb-6 md:text-7xl">
                            {heading}
                        </h2>
                        <p className="md:text-md">{description}</p>
                        <div className="mt-6 md:mt-8">
                            <CtaButton />
                        </div>
                    </m.div>
                    <div className="grid auto-cols-fr grid-cols-1 gap-x-12 gap-y-12 md:grid-cols-[0.5fr_1fr] md:gap-x-20 md:gap-y-16">
                        <m.div
                            variants={slideUp}
                            custom={1}
                            className="flex flex-col gap-y-8 will-change-transform"
                        >
                            {/* NAZWA */}
                            <div className="space-y-2 text-sm">
                                <p className="font-medium text-md uppercase">Nazwa</p>
                                <div className="h-px w-40 bg-black" />
                                <p className="">Agencja Eventowa PassWorld Travels&Events</p>
                            </div>
                            {/* KONTAKT */}
                            <div className="space-y-2 text-sm">
                                <p className="font-medium text-md uppercase">Kontakt</p>
                                <div className="h-px w-40 bg-black" />
                                <TrackedPhoneLink href="tel:+48732493079" className="block hover:underline">
                                    +48 732 493 079
                                </TrackedPhoneLink>
                                <TrackedPhoneLink href="tel:+48732493080" className="block hover:underline">
                                    +48 732 493 080
                                </TrackedPhoneLink>
                                <a href="mailto:kontakt@pass-world.pl" className="block hover:underline">
                                    kontakt@pass-world.pl
                                </a>
                                <p>NIP: 8971869147</p>
                            </div>

                            {/* ADRES */}
                            <div className="space-y-2 text-sm">
                                <p className="font-medium text-md uppercase">Adres</p>
                                <div className="h-px w-40 bg-black" />
                                <p>Oławska 9</p>
                                <p>50-123 Wrocław</p>
                            </div>

                            {/* INFORMACJE */}
                            <div className="space-y-2 text-sm">
                                <p className="font-medium text-md uppercase">Informacje</p>
                                <div className="h-px w-40 bg-black" />
                                <p>Godziny kontaktu: 7-19 (pon-nie)</p>
                                <Link
                                    href="https://maps.google.com/maps?cid=10924208104896587791"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="block hover:underline"
                                >
                                    Nasza wizytówka Google
                                </Link>
                            </div>
                        </m.div>
                        <m.div
                            variants={slideUp}
                            custom={2}
                            className="h-[400px] w-full min-w-0 overflow-hidden rounded-3xl md:h-[516px] will-change-transform"
                        >
                            <iframe
                                src={PASSWORLD_MAP_EMBED_URL}
                                width="100%"
                                height="100%"
                                style={{ border: 0 }}
                                allowFullScreen
                                loading="lazy"
                                referrerPolicy="no-referrer-when-downgrade"
                                title={PASSWORLD_MAP_TITLE}
                                className="size-full"
                            />
                        </m.div>
                    </div>
                </div>
            </m.section>
        </LazyMotion>
    );
};

export const MapSectionDefaults: Props = {
    tagline: "Kontakt",
    heading: "Skontaktuj się z nami we Wrocławiu",
    description: "Nasza siedziba znajduje się w centrum Wrocławia. Skontaktuj się z nami mailowo lub telefonicznie.",
    map: {
        url: "#",
        image: {
            src: "https://relume-assets.s3.us-east-1.amazonaws.com/placeholder-map-image.svg",
            alt: "Relume placeholder map image",
        },
    },
};
